// Rapportvakt — the shell every surface sits in.
//
// Rapportvakt is hosted on utforskadata.se but is not part of it: no platform
// header, no platform footer. The only tie back to the parent is the credit
// line at the bottom. UI chrome is bilingual (sv default); the digest content
// itself is Swedish.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import {
  RAPPORTVAKT_APP_PATH,
  RAPPORTVAKT_DELIVERIES_SORTED,
  RAPPORTVAKT_HELP_PATH,
  RAPPORTVAKT_LANDING_PATH,
  RAPPORTVAKT_PUBLISHERS_PATH,
  rapportvaktSamplePath,
  rapportvaktTabPath,
} from '../../config/rapportvakt';
import { RAPPORTVAKT_ICONS } from '../../config/seoRoutes';
import { Glyph } from './Glyph';
import { RV_STRINGS } from './strings';
import { fetchMe, logoutSession, requestLoginLink } from './api';
import { loadEmail, loadLang, loadSession, saveEmail, saveLang, saveSession, saveWatches } from './storage';
import { track } from '../../lib/analytics';
import './rapportvakt.css';

const PARENT_URL = 'https://utforskadata.se';

// A form-shaped email; good enough to stop an obvious typo before it goes to
// storage. The server is the real judge of a deliverable address.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RvLangContext = createContext('sv');

export function useRvLang() {
  const lang = useContext(RvLangContext);
  return { lang, t: RV_STRINGS[lang] };
}

// The plan's default for a reader with no session at all — an unknown or
// signed-out visitor is the free plan, one watch, same rule the server
// applies to an unknown address.
const DEFAULT_TIER = 'free';
const DEFAULT_LIMIT = 1;

// Real auth now (docs/plan/2026-09-11-magic-link-login.md): the reader signs
// in with a magic link and the browser holds a session bearer token
// (storage.js's rapportvakt.session.v1), not just a cached address. This
// context gives every surface — the top bar, the app page — one shared view
// of that session instead of RapportvaktPage owning it alone, so the header
// can show who is signed in and sign out from anywhere.
const RvAccountContext = createContext({
  status: 'loading',
  email: '',
  tier: DEFAULT_TIER,
  limit: DEFAULT_LIMIT,
  requestLink: async () => ({ ok: false, offline: true, message: '' }),
  logout: () => {},
  refresh: async () => {},
});

export function useRvAccount() {
  return useContext(RvAccountContext);
}

export function BrandMark() {
  return (
    <span className="rv-brand__mark" aria-hidden="true">
      PL
    </span>
  );
}

/**
 * Head tags for a Rapportvakt route. Deliberately not the platform's SEOHead:
 * these pages carry no hreflang pair, and the values come from
 * src/config/seoRoutes.js, the same source scripts/prerender-seo.mjs writes
 * into the static HTML.
 */
function RapportvaktHead({ seo }) {
  if (!seo) return null;
  // The 404 page has no canonical URL — there is no real address for content
  // that does not exist — and carries a noindex/nofollow instead. Everything
  // else in this app sets seo.canonical, so this only branches for that route.
  return (
    <Helmet>
      <html lang="sv" />
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      {seo.canonical ? <link rel="canonical" href={seo.canonical} /> : null}
      {seo.robots ? <meta name="robots" content={seo.robots} /> : null}
      <meta property="og:type" content="website" />
      {seo.canonical ? <meta property="og:url" content={seo.canonical} /> : null}
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:locale" content="sv_SE" />
      <meta property="og:site_name" content="Punktlista" />
      {seo.image ? (
        <meta property="og:image" content={seo.image.url} />
      ) : null}
      {seo.image ? (
        <meta property="og:image:width" content={String(seo.image.width)} />
      ) : null}
      {seo.image ? (
        <meta property="og:image:height" content={String(seo.image.height)} />
      ) : null}
      {seo.image ? <meta property="og:image:alt" content={seo.image.alt} /> : null}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      {seo.image ? <meta name="twitter:image" content={seo.image.url} /> : null}
      {/* Structured data, e.g. the Article JSON-LD getRapportvaktDigestSeo
          builds for a weekly digest (see src/config/seoRoutes.js). Not
          written by scripts/prerender-seo.mjs — that script only rewrites
          the tags above, which every route shares — so a crawler sees this
          once react-helmet-async mounts, same as the rest of this block. */}
      {seo.jsonLd ? (
        <script type="application/ld+json">{JSON.stringify(seo.jsonLd)}</script>
      ) : null}
    </Helmet>
  );
}

/**
 * The whole top bar, right of the wordmark. One cluster, one order, every
 * page — identical on the landing page, the about page and every tab inside
 * the app, except the CTA (see below). Otherwise nothing here depends on the
 * current route: a reader who moves from one Punktlista page to another
 * should never see the rest of the bar's contents jump.
 *
 * Left to right: Om Punktlista, the single primary CTA, the language toggle, then
 * the account control. Mina utskick used to sit here too, but it opened the
 * same app as the CTA right next to it — two links to the same place read as
 * a mistake, not a choice — so it only lives in the footer now. Om tjänsten
 * used to live here as well, but the wordmark already links to the landing
 * page and the footer keeps the link, so the top bar dropped it rather than
 * say the same thing twice. Din plan used to be a link here too; it is
 * account admin now, so it moved into the account control's menu (and stays
 * in the footer for a phone that has not signed in yet). The CTA points at
 * the app root, but only outside the app itself — inside the app it would be
 * a link to where the reader already is, so it is hidden there.
 */
function TopCluster({ t, lang, onToggleLang }) {
  const { pathname } = useLocation();
  const inApp = pathname.startsWith(RAPPORTVAKT_APP_PATH);
  return (
    <div className="rv-top__end">
      {/* Om Punktlista and the CTA both hide under 560px (see rapportvakt.css)
          — the landing page's hero already carries the CTA and the footer
          carries both on every page, so a phone keeps only the language
          toggle and the account control in the bar itself (target A). */}
      <Link className="rv-top__link rv-top__util" to={RAPPORTVAKT_HELP_PATH}>
        {t.help}
      </Link>
      {inApp ? null : (
        <Link className="rv-btn rv-btn--primary rv-top__cta" to={`${RAPPORTVAKT_APP_PATH}/`}>
          {t.open}
        </Link>
      )}
      <button
        type="button"
        className="rv-langbtn"
        onClick={onToggleLang}
        aria-label={lang === 'sv' ? 'Switch to English' : 'Byt till svenska'}
      >
        {lang === 'sv' ? 'EN' : 'SV'}
      </button>
      <AccountControl t={t} />
    </div>
  );
}

/**
 * Escape and an outside click both close an open popover, and either way
 * hands focus back to the button that opened it — a click already leaves
 * focus there, but Escape from inside the popover would otherwise drop it to
 * <body>. Shared by the signed-out popover and the signed-in menu below,
 * since a ghost "Logga in" button and the avatar mark both close the same
 * way.
 */
function usePopoverDismiss(open, onClose, triggerRef, popoverRef) {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      onClose();
      triggerRef.current?.focus();
    };
    const onPointerDown = (event) => {
      if (popoverRef.current?.contains(event.target) || triggerRef.current?.contains(event.target)) {
        return;
      }
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open, onClose, triggerRef, popoverRef]);
}

/**
 * Picks the signed-in or signed-out account control; each owns its popover.
 * `status` starts at 'loading' only when a session is already stored (see
 * the boot state below), in which case `email` is already the cached
 * address from the last sign-in — so the avatar can show right away instead
 * of flashing the ghost "Logga in" button while /auth/me is still in
 * flight. With no stored session at all, `status` starts at 'out' and there
 * is nothing to show optimistically, so the ghost button renders straight
 * away.
 */
function AccountControl({ t }) {
  const { status, email } = useRvAccount();
  if (status === 'out') return <LoginControl t={t} />;
  if (email) return <AccountMenu t={t} email={email} />;
  return null;
}

/**
 * Signed-in: a round mark with the email's first letter, opening a menu to
 * the app's own pages and sign-out. The email sits at the top as plain text
 * — not a link, just so the reader can see whose bevakningar these are.
 */
function AccountMenu({ t, email }) {
  const { logout } = useRvAccount();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  usePopoverDismiss(open, close, triggerRef, popoverRef);

  // Focus lands on the menu itself on open, so Escape has something to close
  // without the reader having tabbed into it first.
  useEffect(() => {
    if (open) popoverRef.current?.focus();
  }, [open]);

  return (
    <div className="rv-account">
      <button
        type="button"
        className="rv-avatar"
        onClick={() => setOpen((current) => !current)}
        aria-label={t.accountLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        ref={triggerRef}
      >
        {email.charAt(0).toUpperCase()}
      </button>
      {open ? (
        <div className="rv-account__menu" role="menu" ref={popoverRef} tabIndex={-1}>
          <p className="rv-account__email" title={email}>
            {email}
          </p>
          <Link className="rv-account__item" role="menuitem" to={rapportvaktTabPath('digest')} onClick={close}>
            <Glyph name="mail" size={15} />
            {t.myDeliveries}
          </Link>
          <Link
            className="rv-account__item"
            role="menuitem"
            to={rapportvaktTabPath('bevakningar')}
            onClick={close}
          >
            <Glyph name="eye" size={15} />
            {t.tabWatches}
          </Link>
          <Link className="rv-account__item" role="menuitem" to={rapportvaktTabPath('plan')} onClick={close}>
            <Glyph name="card" size={15} />
            {t.tabPlan}
          </Link>
          <div className="rv-account__divider" role="separator" />
          <button
            type="button"
            className="rv-account__item"
            role="menuitem"
            onClick={() => {
              logout();
              close();
              triggerRef.current?.focus();
            }}
          >
            <Glyph name="logout" size={15} />
            {t.logoutBtn}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Signed-out: a ghost button opening a small sign-in popover — an email
 * input, same skin as a catalogue row's pick form, plus a one-line hint.
 * Submitting asks the server for a login link (no password, ever); once that
 * succeeds the form is replaced, inside the same popover, by the "check your
 * mail" line and a ghost "Skicka igen" button, and the popover stays open
 * until the reader dismisses it — there is nothing left to submit.
 */
function LoginControl({ t }) {
  const { requestLink } = useRvAccount();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  // '' until a link has actually been sent; the address it went to after.
  const [sentTo, setSentTo] = useState('');
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const inputRef = useRef(null);
  const close = useCallback(() => {
    setOpen(false);
    setSentTo('');
  }, []);
  usePopoverDismiss(open, close, triggerRef, popoverRef);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const submit = async (event) => {
    event.preventDefault();
    if (pending) return;
    const trimmed = value.trim();
    if (!EMAIL_PATTERN.test(trimmed)) {
      setError(t.loginError);
      return;
    }
    setError('');
    setPending(true);
    const result = await requestLink(trimmed);
    setPending(false);
    if (!result.ok) {
      setError(result.message || t.offlineNote);
      return;
    }
    track('login_link_sent');
    setSentTo(trimmed);
  };

  const resend = async () => {
    if (pending) return;
    setPending(true);
    const result = await requestLink(sentTo);
    setPending(false);
    if (!result.ok) setError(result.message || t.offlineNote);
  };

  return (
    <div className="rv-account">
      <button
        type="button"
        className="rv-btn rv-btn--ghost"
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="dialog"
        aria-expanded={open}
        ref={triggerRef}
      >
        {t.loginBtn}
      </button>
      {open ? (
        <div className="rv-account__menu" role="dialog" aria-label={t.loginBtn} ref={popoverRef} tabIndex={-1}>
          {sentTo ? (
            <>
              <p className="rv-pickform__hint">{t.loginSent(sentTo)}</p>
              <button type="button" className="rv-btn rv-btn--ghost" onClick={resend} disabled={pending}>
                {pending ? `${t.sending}…` : t.resend}
              </button>
              {error ? <p className="rv-pickform__error">{error}</p> : null}
            </>
          ) : (
            <>
              <form className="rv-pickform" onSubmit={submit}>
                <div className="rv-pickform__row">
                  <input
                    className="rv-input"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder={t.loginPlaceholder}
                    value={value}
                    onChange={(event) => {
                      setValue(event.target.value);
                      setError('');
                    }}
                    aria-label={t.loginEmailLabel}
                    ref={inputRef}
                    disabled={pending}
                  />
                  <button type="submit" className="rv-btn rv-btn--primary" disabled={pending}>
                    {pending ? `${t.sending}…` : t.loginBtn}
                  </button>
                </div>
                {error ? <p className="rv-pickform__error">{error}</p> : null}
              </form>
              <p className="rv-pickform__hint">{t.loginHint}</p>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

function ShellFooter({ t }) {
  const { pathname } = useLocation();
  const inApp = pathname.startsWith(RAPPORTVAKT_APP_PATH);
  return (
    <footer className="rv-foot">
      <div className="rv__inner">
        <div className="rv-foot__cols">
          <div className="rv-foot__brand">
            <Link className="rv-brand" to={RAPPORTVAKT_LANDING_PATH}>
              <BrandMark />
              <span className="rv-brand__name">Punktlista</span>
            </Link>
            <p className="rv-foot__line">{t.footerLine}</p>
          </div>

          <div className="rv-foot__col">
            <h2 className="rv-foot__head">{t.footerService}</h2>
            <Link className="rv-foot__link" to={RAPPORTVAKT_LANDING_PATH}>
              {t.about}
            </Link>
            {/* The top bar's CTA hides under 560px (target A) — this is the
                phone's way into the app from the footer, on every page except
                inside the app itself, where it would link to the current
                page. */}
            {inApp ? null : (
              <Link className="rv-foot__link" to={`${RAPPORTVAKT_APP_PATH}/`}>
                {t.open}
              </Link>
            )}
            <Link className="rv-foot__link" to={rapportvaktTabPath('bevakningar')}>
              {t.tabWatches}
            </Link>
            {/* Mina utskick never sat in the top bar (see TopCluster's own
                comment) and Din plan moved into the account control's menu,
                so the footer is the one place both stay reachable without
                first signing in. */}
            <Link className="rv-foot__link" to={rapportvaktTabPath('digest')}>
              {t.myDeliveries}
            </Link>
            <Link className="rv-foot__link" to={rapportvaktTabPath('plan')}>
              {t.tabPlan}
            </Link>
            <Link className="rv-foot__link" to={RAPPORTVAKT_PUBLISHERS_PATH}>
              {t.publishers}
            </Link>
          </div>

          <div className="rv-foot__col">
            <h2 className="rv-foot__head">{t.footerSamples}</h2>
            {RAPPORTVAKT_DELIVERIES_SORTED.map((delivery) => (
              <Link
                className="rv-foot__link"
                key={delivery.slug}
                to={rapportvaktSamplePath(delivery.slug)}
              >
                {delivery.title}
              </Link>
            ))}
          </div>

          <div className="rv-foot__col">
            <h2 className="rv-foot__head">{t.footerContact}</h2>
            {/* No printed address here: the site is prerendered to static
                HTML, so a mailto: link in this markup would ship the address
                straight into a page any crawler can read. Both address links
                land on the about page instead — that page is the one place
                the address is offered, behind a button rather than a link
                (see RapportvaktHelpPage.jsx). */}
            <Link className="rv-foot__link" to={RAPPORTVAKT_HELP_PATH}>
              {t.help}
            </Link>
            <Link className="rv-foot__link" to={`${RAPPORTVAKT_HELP_PATH}#faq`}>
              {t.helpFaqLink}
            </Link>
            <Link className="rv-foot__link" to={RAPPORTVAKT_HELP_PATH}>
              {t.writeToOsama}
            </Link>
            <a
              className="rv-foot__link"
              href={PARENT_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.footer}
            </a>
          </div>
        </div>

        <div className="rv-foot__bottom">{t.footerRights}</div>
      </div>
    </footer>
  );
}

export default function RapportvaktShell({ seo = null, children }) {
  const [lang, setLang] = useState('sv');
  // Account state, kept here so every surface (top bar, app page) shares one
  // view of it. `status` starts 'loading' only when a session is already on
  // file — see the initializers below — so a reader who has never signed in
  // gets 'out' on the very first paint, no flash of anything else.
  const [status, setStatus] = useState(() => (loadSession() ? 'loading' : 'out'));
  const [email, setEmail] = useState(() => {
    const session = loadSession();
    return (session && session.email) || loadEmail();
  });
  const [tier, setTier] = useState(() => {
    const session = loadSession();
    return session && typeof session.tier === 'string' ? session.tier : DEFAULT_TIER;
  });
  const [limit, setLimit] = useState(() => {
    const session = loadSession();
    return session && 'limit' in session ? session.limit : DEFAULT_LIMIT;
  });
  const { pathname, hash } = useLocation();

  /**
   * Confirm whatever the stored session claims against /auth/me. Called on
   * mount and again by `refresh` — after a sign-in (RapportvaktLoginPage) or
   * whenever an authed call elsewhere notices a 401 (api.js already forgot
   * the stored session at that point; this is what makes the rest of the UI
   * notice too). A network failure keeps the current, already-optimistic
   * state rather than bouncing a reader to signed-out on a hiccup.
   */
  const refresh = useCallback(async () => {
    if (!loadSession()) {
      setStatus('out');
      setEmail('');
      setTier(DEFAULT_TIER);
      setLimit(DEFAULT_LIMIT);
      return;
    }
    const result = await fetchMe();
    if (result.ok) {
      setEmail(result.email);
      setTier(result.tier);
      setLimit(result.limit);
      setStatus('in');
      saveEmail(result.email);
      return;
    }
    if (result.status === 401) {
      setEmail('');
      setTier(DEFAULT_TIER);
      setLimit(DEFAULT_LIMIT);
      setStatus('out');
      return;
    }
    setStatus('in');
  }, []);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLang(loadLang());
    const root = document.documentElement;
    root.setAttribute('data-rv', 'true');

    // The platform's favicons are static tags in index.html and Helmet can only
    // add tags, not remove them, so a browser would be left choosing between
    // two marks. Take the platform's out for as long as a Punktlista route is
    // mounted and put them back on the way out — the same thing
    // scripts/prerender-seo.mjs does to the static HTML. Helmet's own tags carry
    // data-rh, which is what keeps this from removing ours.
    const platformIcons = [
      ...document.head.querySelectorAll(
        'link[rel="icon"]:not([data-rh]), link[rel="apple-touch-icon"]:not([data-rh])'
      ),
    ];
    for (const link of platformIcons) link.remove();

    return () => {
      root.removeAttribute('data-rv');
      for (const link of platformIcons) document.head.appendChild(link);
    };
  }, []);

  // React Router keeps the scroll position across a navigation, which put a
  // reader who clicked a publisher in the landing page's logo strip — near the
  // bottom of a long page — straight into that publisher's footer. Scoped to
  // Punktlista, where every route change is a new page rather than a filter.
  // Skipped when the new URL carries a hash (e.g. /om/#faq): a page with a
  // section to jump to scrolls itself there in its own effect, and this
  // effect fires after that child one (parent effects run after their
  // children on the same commit) — running it unconditionally would always
  // win and snap the reader back to the top.
  useEffect(() => {
    if (hash) return;
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  const toggleLang = () => {
    const next = lang === 'sv' ? 'en' : 'sv';
    setLang(next);
    saveLang(next);
    track('lang_toggle', { to: next });
  };

  // Caches the address as the display hint (Shell.jsx's own docstring on
  // EMAIL_KEY) and asks the server for a login link — an intent
  // (`{ series_slug, frequency? }`) also activates that catalogue pick the
  // moment the link is clicked. Never signs anyone in by itself: nothing
  // changes until the reader opens the mailed link on /logga-in.
  const requestLink = useCallback(async (nextEmail, intent) => {
    saveEmail(nextEmail);
    return requestLoginLink(nextEmail, intent);
  }, []);

  // Forgets the session and every watch cached under it, the same reset
  // RapportvaktPage's own sign-out used to do — the one place that clears
  // both, since the header can sign out from any page, not only from Din
  // plan. Best-effort on the server (logoutSession never throws); the local
  // state clears either way, so a reader can always sign out offline.
  const logout = useCallback(() => {
    logoutSession();
    saveSession(null);
    saveWatches([]);
    setStatus('out');
    setEmail('');
    setTier(DEFAULT_TIER);
    setLimit(DEFAULT_LIMIT);
  }, []);

  const account = useMemo(
    () => ({ status, email, tier, limit, requestLink, logout, refresh }),
    [status, email, tier, limit, requestLink, logout, refresh]
  );

  const t = RV_STRINGS[lang];

  return (
    <RvAccountContext.Provider value={account}>
      <RvLangContext.Provider value={lang}>
        <div className="rv">
          {/* Icons sit here rather than in RapportvaktHead so every Punktlista
              surface gets the product's own mark in the tab, including any route
              that renders without an seo object. Helmet appends after the
              platform's static icon links, and the last match is what a browser
              uses; the prerendered HTML removes the platform's outright. */}
          <Helmet>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
            <link
              rel="stylesheet"
              href="https://fonts.googleapis.com/css2?family=Source+Serif+4:wght@400;500;600;700&display=swap"
            />
            {RAPPORTVAKT_ICONS.map((icon) => (
              <link key={icon.href} {...icon} />
            ))}
          </Helmet>
          <RapportvaktHead seo={seo} />

          <header className="rv-top">
            <div className="rv__inner rv-top__row">
              <Link className="rv-brand" to={RAPPORTVAKT_LANDING_PATH}>
                <BrandMark />
                <span className="rv-brand__name">Punktlista</span>
              </Link>
              <TopCluster t={t} lang={lang} onToggleLang={toggleLang} />
            </div>
          </header>

          {children}

          <ShellFooter t={t} />
        </div>
      </RvLangContext.Provider>
    </RvAccountContext.Provider>
  );
}
